import { useMemo } from 'react';
import { getPersonnelStorage } from '@personnel/composition';

/**
 * Hook for accessing current user context within Personnel domain
 *
 * Provides a facade over infrastructure storage for current user data.
 * Includes utility methods for common user checks.
 *
 * @returns {Object} Current user context
 * @returns {string} returns.uuid - Current user's UUID
 * @returns {Function} returns.isSelf - Check if a UUID matches current user
 *
 * @example
 * const { uuid, isSelf } = useCurrentUser();
 *
 * if (isSelf(employee.uuid)) {
 *   // Cannot delete self
 * }
 */
export const useCurrentUser = () => {
  const uuid = getPersonnelStorage().getCurrentUserUuid();

  const context = useMemo(
    () => ({
      /**
       * Current user's UUID
       */
      uuid,

      /**
       * Check if the given UUID belongs to the current user
       *
       * @param targetUuid - UUID to compare against current user
       * @returns true if the UUID matches the current user
       */
      isSelf: (targetUuid: string): boolean => uuid === targetUuid,
    }),
    [uuid],
  );

  return context;
};
